// aiSkills/skillRegistry — Runtime skill-playbooks. Inte att förväxla med
// skillCatalog.ts som är content-snippets för system-prompten. En "skill"
// här är en MULTI-STEG-orchestration som kombinerar flera tool-calls till
// en high-level action.
//
// AI:n kan invoca en skill via `run_skill` tool (TODO: wire:as i edge-fn).
// Varje skill returnerar en ExecutionPlan — listan av steg den EXPECTED
// kommer köra. Skillen själv KÖR inga tools direkt; dispatcher:n (som
// i framtiden är builderAgent) gör det.
//
// Denna separation gör skills rena + unit-testbara: input → ExecutionPlan.

import type {
  MultiSheetWorkflowState,
} from '@/types/workflow';

export type SkillName =
  | 'diagramFromPrompt'
  | 'diagramFromCodebase'
  | 'diagramFromSupabaseSchema'
  | 'refactorLayout'
  | 'splitIntoLayers';

/**
 * Ett steg i skill-execution-planen. Respektive step mappas mot ett
 * tool-name som existerar i AI_TOOLS ∪ EXTENDED_TOOLS.
 */
export interface SkillStep {
  /** Ordning i planen (0-indexed). */
  index: number;
  /** Tool-name som detta step ska trigga. */
  tool: string;
  /** Args till tool:en (passas till validateToolCall). */
  args: Record<string, unknown>;
  /** Människoläsbar beskrivning för UI-stepper. */
  description: string;
}

export interface ExecutionPlan {
  skill: SkillName;
  goal: string;
  steps: SkillStep[];
  /** Estimerad credit-kostnad (input-storlek-beroende). */
  estimatedCost: number;
}

export interface SkillContext {
  /** Det user-prompt som triggade skillen. */
  userPrompt: string;
  /** Existing state om skillen ska extend:a. Undefined = skapa från noll. */
  currentState?: MultiSheetWorkflowState;
  /** Språk för output-strängar (sv default). */
  language?: 'sv' | 'en';
}

export interface Skill {
  name: SkillName;
  description: string;
  /** Matcher för när skillen ska föreslås automatiskt. */
  trigger: (prompt: string) => boolean;
  /** Rent: input → exec-plan. Ingen tool-execution. */
  plan: (ctx: SkillContext) => ExecutionPlan;
  /**
   * AB1: Skillen får ENDAST triggas av admin-users. Används för
   * skills som läser eller exponerar källkod / DB-schema / andra
   * privilegierade resurser som aldrig ska nå end-users.
   *
   * - suggestSkills(prompt, { isAdmin: false }) filtrerar bort dessa.
   * - runtime-dispatchers kontrollerar flag:en innan plan-execution.
   * - Default false (öppen skill).
   */
  requiresAdmin?: boolean;
}

const REGISTRY: Partial<Record<SkillName, Skill>> = {};

export function registerSkill(skill: Skill): void {
  REGISTRY[skill.name] = skill;
}

export function getSkill(name: SkillName): Skill | null {
  return REGISTRY[name] ?? null;
}

export function allSkills(): Skill[] {
  return Object.values(REGISTRY).filter(Boolean) as Skill[];
}

export interface SuggestOptions {
  /** AB1: Om true → inkludera admin-only-skills. Default false (publik user). */
  isAdmin?: boolean;
}

/**
 * Givet en user-prompt, returnera de skills som matcher trigger-mönstret.
 * Används av chat-UI:t för att visa "Build diagram"-quick-action-knappar.
 *
 * AB1: admin-only-skills filtreras bort när `isAdmin=false` (default).
 */
export function suggestSkills(prompt: string, opts: SuggestOptions = {}): Skill[] {
  const { isAdmin = false } = opts;
  return allSkills().filter((s) => {
    if (s.requiresAdmin && !isAdmin) return false;
    return s.trigger(prompt);
  });
}

/**
 * AB1: Defensiv guard — caller kontrollerar att user faktiskt har
 * admin-rättigheter innan den kör en admin-only-skill. Returnerar
 * null om skillen är admin-only och isAdmin=false.
 */
export function getSkillForUser(name: SkillName, isAdmin: boolean): Skill | null {
  const skill = REGISTRY[name];
  if (!skill) return null;
  if (skill.requiresAdmin && !isAdmin) return null;
  return skill;
}
